import React, { useCallback, useState } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import defaultProfileBg from '../../../../assets/images/default_profile_bg.svg';
import defaultProfileImg from '../../../../assets/images/default_profile_img.svg';
import CameraIcon from '../../../../assets/svgComponents/CameraIcon';
import EditIconWithBackground from '../../../../assets/svgComponents/EditIconWithBackground';
import LinkedlnIconImage from '../../../../assets/svgComponents/LinkedlnIconImage';
import ThreeDotsIconImage from '../../../../assets/svgComponents/ThreeDotsIconImage';
import WebsiteIconImage from '../../../../assets/svgComponents/WebsiteIconImage';
import Notification from '../../../../components/Notification/Notification';
import { MAX_IMAGE_SIZE } from '../../../../constants/commonConstants';
import { deleteCoverPicture, updateProfilePicture } from '../../../../redux/actions/userActions';
import urls from '../../../../routing/urls';

import { AddBackgroundImageModal } from './AddBackgroundImageModal/AddBackgroundImageModal';
import { EditProfileBio } from './EditProfileBio/EditProfileBio';

import style from './ProfileHeader.module.scss';

const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
  <button
    type="button"
    className={style.dropDownButton}
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
  >
    {children}
  </button>
));

const ProfileHeader = ({ currentUser, handleProfileUpdate, isPublic }) => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [profileImage, setProfileImage] = useState(currentUser?.avatar_thumb || defaultProfileImg);
  const [showUploadImageModal, setShowUploadImageModal] = useState(false);
  const [showEditBioModal, setShowEditBioModal] = useState(false);
  const linksArray = currentUser?.links_json ? JSON.parse(currentUser?.links_json) : [];
  const linkedin = linksArray.filter((link) => link.type === 'ln');

  const handleFileUpload = useCallback(
    (event) => {
      if (event.target.files && event.target.files[0]) {
        if (event.target.files[0].size > MAX_IMAGE_SIZE) {
          Notification.error(t('validation.fileSizeExceeds'));
          return;
        }
        dispatch(
          updateProfilePicture(event.target.files, 'user_avatar', currentUser.id, () =>
            setProfileImage(URL.createObjectURL(event.target.files[0]))
          )
        );
      }
    },
    [setProfileImage, updateProfilePicture, currentUser.id]
  );

  return (
    <div className={`${style.container} d-flex flex-column`}>
      <div className={style.bgImageDiv}>
        <AddBackgroundImageModal
          showModal={showUploadImageModal}
          setShowModal={setShowUploadImageModal}
          currentUser={currentUser}
          updateProfilePicture={updateProfilePicture}
          deleteCoverPicture={deleteCoverPicture}
        />
        <img src={currentUser.cover_image || defaultProfileBg} className={style.bgImage} alt="profile-bg" />
        {!isPublic && (
          <button className={style.editBgImageButton} onClick={() => setShowUploadImageModal(true)} type="button">
            <EditIconWithBackground />
          </button>
        )}
      </div>
      <div className="d-flex m-0 pb-2">
        <div className={`col-sm-3 ${style.profileImageDiv}`}>
          {!isPublic && (
            <span className={style.cameraIcon}>
              <CameraIcon />
              <input type="file" accept="image/*" onChange={handleFileUpload} />
            </span>
          )}
          <img
            src={profileImage || currentUser.avatar_thumb}
            alt="profile-bg"
            onError={() => setProfileImage(defaultProfileImg)}
            className={style.profileImage}
          />
        </div>
        <div className={`col-sm-9 ${style.profileSection}`}>
          <div className={`flex-column ${style.userInfo}`}>
            <h3>{currentUser.full_name}</h3>
            {currentUser.company_position && <h5>{currentUser.company_position}</h5>}
            <h6>{currentUser.full_address}</h6>
            <ul className="list-inline m-0 mt-2">
              {linkedin[0]?.value && linkedin[0]?.public !== false ? (
                <li className="list-inline-item">
                  <a href={linkedin[0].value} target="_blank" rel="noreferrer">
                    <div className={style.iconOnHover}>
                      <LinkedlnIconImage />
                    </div>
                  </a>
                </li>
              ) : (
                ''
              )}
              {linksArray.map(
                (el) =>
                  el.type !== 'ln' &&
                  el.public !== false && (
                    <li className="list-inline-item" key={`${el.type + el.value}`}>
                      <a
                        href={
                          el.value.includes('http') || el.value.includes('https') ? el.value : `https://${el.value}`
                        }
                        target="_blank"
                        rel="noreferrer"
                      >
                        <div className={style.iconOnHover}>
                          <WebsiteIconImage />
                        </div>
                      </a>
                    </li>
                  )
              )}
            </ul>
          </div>
        </div>
        {!isPublic && (
          <div className={style.editIcon}>
            <Dropdown>
              <Dropdown.Toggle as={CustomToggle}>
                <ThreeDotsIconImage />
              </Dropdown.Toggle>
              <EditProfileBio
                showModal={showEditBioModal}
                handleProfileUpdate={handleProfileUpdate}
                currentUser={currentUser}
                setShowModal={setShowEditBioModal}
              />
              <Dropdown.Menu className={style.dropdownMenu} align="end">
                <Dropdown.Item bsPrefix={style.dropdownItem} onClick={() => setShowEditBioModal(true)}>
                  {t('edit')}
                </Dropdown.Item>
                <Dropdown.Item bsPrefix={style.dropdownItem} onClick={() => navigate(urls.publicProfile)}>
                  {t('profile.viewAs')}
                </Dropdown.Item>
                <Dropdown.Item bsPrefix={style.dropdownItem} onClick={() => navigate(urls.linkedinProfileImport)}>
                  {t('profile.importLinkedinProfile')}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileHeader;
